<template>

    <div class="row mb-2">
      <div class="col">
        <button class="btn btn-secondary btn-sm mb-2" type="button" @click="$window.location.reload()" title="Osveži">
          ⟳ Osveži
        </button>
      </div>
    </div>

    <div class="row mb-2">
      <div class="col-md-8">
        <div class="row">
          <label class="col-form-label col-sm-2">Delavec</label>
          <div class="col-sm-10">
            <SelectEntity apiEntity="DropDown/Workers" v-model="filters.ownerID" />
            <small v-if="worker?.vehicleRegistrationNumber" class="text-muted">Trenutno izbrano skladišče: <strong>{{ worker.vehicleRegistrationNumber }}</strong></small>
            &nbsp;<small v-if="currentLocation" class="text-muted">
              <span v-if="currentLocation.opis">{{ currentLocation.opis }} </span>
              <span v-if="currentLocation.stopduration"> Postanek: {{ currentLocation.stopduration }} min</span>
            </small>
          </div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="row">
          <div class="col-sm-4">Hitri filter:</div>
          <div class="col">
            <div class="form-check" >
              <input class="form-check-input" type="checkbox" id="completedOrFinalizedYesterday" v-model="filters.completedOrFinalizedYesterday" :disabled="planningEnabled">
              <label class="form-check-label" for="completedOrFinalizedYesterday">Končani/zaključeni včeraj</label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="completedOrFinalizedToday" v-model="filters.completedOrFinalizedToday" :disabled="planningEnabled">
              <label class="form-check-label" for="completedOrFinalizedToday">Končani/zaključeni danes</label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="showTodayVehiclePath" v-model="showTodayVehiclePath">
              <label class="form-check-label" for="showTodayVehiclePath">Pokaži današnjo pot vozila
                <span v-show="showTodayVehiclePath && !filters.ownerID">(izberi delavca!)</span>
              </label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="planningEnabled" v-model="planningEnabled">
              <label class="form-check-label" for="planningEnabled">Planiranje poti
                <span v-if="planningEnabled && !filters.ownerID" class="text-warning">(izberi delavca!)</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row mb-3">
      <div class="col-md-4">
        <div class="row">
          <div class="col-sm-4">
            Prioriteta:
          </div>
          <div class="col">
            <IntegerRadioGroup v-model="filters.priority" :numberOfValues=3 />
          </div>
        </div>
        <BoolFilter label="Posebnosti" name="isSpecial" v-model="filters.isSpecial" />
        <BoolFilter label="Pozornost" name="isAttention" v-model="filters.isAttention" />
        <BoolFilter label="Garancija" name="warranty" v-model="filters.isWarranty" />
        <BoolFilter label="Potrjen" name="isAccepted" v-model="filters.isAccepted" />
        <BoolFilter label="Delni kom." name="hadUncompletedCommission" v-model="filters.hadUncompletedCommission" />
        <BoolFilter label="Avtom. razdalja" name="distanceAssignment" v-model="filters.isDistanceAssignment" />
        <BoolFilter label="Del periodike" name="isGeneratedPeriodic" v-model="filters.isGeneratedPeriodic" />
        <BoolFilter label="Datum obiska" name="hasDatePlannedVisit" v-model="filters.hasDatePlannedVisit" />
      </div>
      <div class="col-md-4">
        <PeriodicsFilter :filters="filters" />
      </div>
      <div class="col-md-4">
        <SelectStatus :statuses="statuses" v-model="filters.status" @change="$toggleMultiSelect(filters, 'status', $event)" :multiple="true" />
      </div>
    </div>
    <div class="row mb-3">
      <div class="col">
        <DateTimeFilterSet v-model="filters" />
      </div>
    </div>
    <div class="row mb-3">
      <div class="col">
        <GoogleMap mapId="mapEl"
          :api-key="$window.GOOGLE_API_KEY"
          style="width: 100%; height: calc(100vh - 500px); min-height: 400px;"
          :center="center" :zoom="8" ref="mapEl">
          <AdvancedMarker v-for="(marker, idx) in markers"
            :key="idx"
            :options="marker.options"
            @gmp-click="selectedMarker = marker" />
          <AdvancedMarker v-for="(vehicleMarker, idx) in vehicleMarkers"
            :key="idx"
            :options="vehicleMarker.options"
            @gmp-click="selectedVehicleMarker = vehicleMarker" />
          <AdvancedMarker v-for="(marker, idx) in homeMarkers"
            :key="idx"
            :options="marker.options"  />
          <Polyline v-if="showTodayVehiclePath && vehicleHistory.length > 1"
            :options="{ path: vehicleHistory, strokeColor: '#48ff00de', strokeOpacity: 0.8, strokeWeight: 3 }" />
          <Polyline v-for="(leg, idx) in todayRouteLegs"
            :key="'leg-' + idx"
            :options="{ path: leg.path, strokeColor: '#0d31fd', strokeOpacity: 0.9, strokeWeight: 2, icons: leg.icons }" />
          <AdvancedMarker v-for="(hm, idx) in historyEndpointMarkers" v-if="showTodayVehiclePath"
            :key="'he-' + idx"
            :options="hm.options" />
        </GoogleMap>
      </div>
      <div class="col-md-5" v-show="showRoutePlanning" style="font-size: 0.7rem;">
        <div class="mb-2">
          <label class="text-muted me-2">Dan za planiranje:</label>
          <div v-for="day in $createQuickDates(-2, 2)" :key="day.offset" class="form-check">
            <input class="form-check-input" type="radio" :id="'pd-' + day.offset" :value="day.isoDate" v-model="selectedPlanningDay">
            <label class="form-check-label" :for="'pd-' + day.offset" :class="{ 'fw-bold': day.isToday, 'text-muted': day.isPast }">{{ day.label }}</label>
          </div>
        </div>
        <hr class="my-2">
        <div class="mb-2">
          <small class="text-muted me-2">Izhodišče:</small>
          <div class="form-check" v-if="currentLocation">
            <input class="form-check-input" type="radio" id="spVehicle" value="vehicle" v-model="selectedStartingPoint">
            <label class="form-check-label" for="spVehicle">Trenutna lokacija vozila</label>
            {{ currentLocation.kraj1 }}
          </div>
          <div v-if="worker?.lat && worker?.lng" class="form-check">
            <input class="form-check-input" type="radio" id="spHome" value="home" v-model="selectedStartingPoint">
            <label class="form-check-label" for="spHome">Dom delavca</label>
          </div>
          <div class="form-check">
            <input class="form-check-input" type="radio" id="spAjdovscina" value="ajdovscina" v-model="selectedStartingPoint">
            <label class="form-check-label" for="spAjdovscina">Skladišče Ajdovščina</label>
          </div>
          <div class="form-check">
            <input class="form-check-input" type="radio" id="spZalec" value="zalec" v-model="selectedStartingPoint">
            <label class="form-check-label" for="spZalec">Skladišče Žalec</label>
          </div>

        </div>
        <hr class="my-2">
        <div class="mb-2">
          <div ref="addressSearchContainer" class="mt-1 mb-2"></div>

          <button v-for="wp in extraWaypoints" :key="wp.name" class="btn btn-sm btn-outline-secondary me-1 mb-1"
            @click="addCustomWaypoint({ target: { value: extraWaypoints.indexOf(wp) } })">+ {{ wp.name }}</button>
          <button class="btn btn-sm btn-outline-primary me-1 mb-1 text-end" @click="addAddressToWaypoints" :disabled="!selectedPlace">Dodaj naslov</button>
        </div>
        <hr class="my-2">
        <table class="table table-sm mb-0" v-show="waypoints.length">
          <tbody>
            <tr>
              <th>Pri.</th>
              <th>Objekt</th>
              <th>Čas</th>
              <th>Razdalja</th>
              <th>Zap.</th>
              <th>Pot</th>
              <th>Ocena</th>
              <th></th>
            </tr>
            <tr v-for="wp in waypoints" :key="wp.id"
                draggable="true"
                :class="{ 'table-success': wp.serviceOrderIsCompleted, 'table-warning': wp.serviceOrderIsInProgress, 'table-info': dragState.overId === wp.id && dragState.draggingId !== wp.id, 'text-secondary': isGray(wp) }"
                style="cursor: grab;"
                @dragstart="onDragStart($event, wp.id)"
                @dragover="onDragOver($event, wp.id)"
                @drop="onDrop($event, wp.id)"
                @dragend="onDragEnd">
                <td @click="!wp.serviceOrderID ? null : $window.open($router.resolve({ name: 'ServiceOrder', params: { workOrderId: wp.serviceOrderID } }).href, '_blank')"
                  role="button" class="bg-light">
                  {{ wp.serviceOrderPriority }}<span v-if="wp.serviceOrderIsSpecial">+</span>
                </td>
                <td>{{ wp.name }}</td>
                <td class="text-nowrap">{{ $formatTime(wp.time) }}</td>
                <td class="text-nowrap text-muted">
                  <span v-if="todayRouteLegByOrderId[wp.id]">{{ Math.round(todayRouteLegByOrderId[wp.id]?.distanceMeters / 1000) }} km</span>
                </td>
                <td><span class="p-2 text-muted">{{ wp.sequence }}</span></td>
                <td class="text-nowrap">{{ $formatDuration(todayRouteLegByOrderId[wp.id]?.durationSeconds) }}</td>
                <td class="text-nowrap">
                  <span v-if="wp.serviceOrderID" class="ms-1 text-muted">{{ $formatDuration(wp.stopSeconds) || 'N/A' }}</span>
                  <span v-else-if="editingTimeId !== wp.id" role="button" class="ms-1 text-primary" style="cursor:pointer; text-decoration:underline dotted;" @click="startEditingTime(wp)">
                    {{ $formatDuration(wp.stopSeconds) || '0m' }}
                  </span>
                  <span v-else class="ms-1">
                    <input type="number" min="0" step="5" max="600" class="form-control form-control-sm d-inline-block" style="width: 70px;" v-model.number="editTimeMinutes" @keyup.enter="saveEditingTime(wp)" :ref="setTimeInputRef" />
                    <button class="btn btn-sm btn-outline-success py-0 px-1" @click="saveEditingTime(wp)" title="Shrani">&#10003;</button>
                  </span>
                </td>
                <td>
                  <span class="text-danger" role="button" style="cursor:pointer" @click="waypoints = waypoints.filter(w => w.id !== wp.id)">&times;</span>
                </td>
              </tr>
          </tbody>
          <tfoot>
            <tr class="fw-bold">
              <td colspan="3">Skupaj</td>
              <td><span v-if="totals.roadDistanceMeters">{{ Math.round(totals.roadDistanceMeters / 1000) }} km</span></td>
              <td></td>
              <td><span v-if="totals.roadSeconds">{{ $formatDuration(totals.roadSeconds) }}</span></td>
              <td><span v-if="totals.workSeconds">{{ $formatDuration(totals.workSeconds) }}</span></td>
              <td></td>
            </tr>
            <tr class="fw-bold" v-if="totals.roadSeconds || totals.workSeconds">
              <td colspan="6">Skupaj (pot+delo):</td>
              <td colspan="2"><strong>{{ $formatDuration((totals.roadSeconds || 0) + (totals.workSeconds || 0) + (totals.bufferSeconds || 0)) }}</strong></td>
            </tr>
          </tfoot>
        </table>
        <div class="mb-2 mt-2" v-show="waypoints.length">
          <button class="btn btn-sm btn-success" @click="saveCurrentWaypointsAsync" :disabled="isPastDay">Shrani pot</button> preden spremeniš filtre.
          <br />Ko je enkrat pot shranjena in se spremeni datum obiska na SDN, to ne bo spremenilo poti - pot je treba ročno popraviti. Enako velja za oceno časa.
        </div>
        <div class="mt-3" v-if="isToday && waypointsSaved && selectedStartingPoint === 'vehicle'">
          <button class="btn btn-sm btn-outline-primary" @click="sendDepartureEmailsAsync" :disabled="sendingDepartureEmails">
            {{ sendingDepartureEmails ? 'Pošiljanje…' : '📧 Pošlji obvestila' }}
          </button>
          <small class="text-muted d-block mt-1">Pošlje današnja e-obvestila (štart + obvestila strankam) za izbranega delavca.</small>
        </div>
      </div>
    </div>
    <Modal :title="'Delovni nalogi'" :visible="!!selectedMarker" @close="selectedMarker=null">
      <div class="row">
        <div class="col">
          <WorkOrdersList
            :workOrders="selectedMarker?.orders"
            @assignTo="assignToAsync"
            @setDatePlannedVisit="handleSetDatePlannedVisitAsync"
            />
        </div>
      </div>
    </Modal>
    <Modal :title="'Vozilo'" :visible="!!selectedVehicleMarker" @close="selectedVehicleMarker=null">
      <div class="row">
        <div class="col" v-if="selectedVehicleMarker?.vehicle">
          <h5>{{ selectedVehicleMarker?.vehicle?.opis }}</h5>
          <p><strong>Lokacija:</strong> {{ selectedVehicleMarker?.vehicle?.kraj1 }}</p>
          <p><strong>Zadnji signal:</strong> {{ new Date(selectedVehicleMarker?.vehicle?.cas).toLocaleString() }}</p>
          <p><strong>Čas postanka:</strong> {{ selectedVehicleMarker?.vehicle?.stopduration }} min</p>
          <p><strong>Števec vozila:</strong> {{ selectedVehicleMarker?.vehicle?.vehiclecounter }}</p>
          <h4><strong>Zadnji delavec:</strong> {{ selectedVehicleMarker?.vehicle?.currentWorker?.firstName }} {{ selectedVehicleMarker?.vehicle?.currentWorker?.lastName }}</h4>
          <p><strong>ID naprave:</strong> {{ selectedVehicleMarker?.vehicle?.idNaprava }}</p>
        </div>
      </div>
    </Modal>
</template>

<script setup>
/* global VueRouter, google */
import { ref, toRef, toRefs, computed, defineComponent, watch, reactive, onMounted, onUnmounted, nextTick } from "vue"
import { setError, setWarning, setSuccess, startLoading, stopLoading } from "../components/Notification"
import { useWorkOrders } from "../composition-api/useWorkOrders"
import { useWorkOrder } from "../composition-api/useWorkOrder"
import { useEnums } from "../composition-api/useEnums"
import { GoogleMap, AdvancedMarker, Polyline } from "vue3-google-map"
import WorkOrdersList from "../components/WorkOrdersList.vue"
import PeriodicsFilter from "../components/PeriodicsFilter.vue"
import {
  listEntitiesAsync,
  getEntityAsync,
  saveWaypointsAsync,
  patchWorkOrderAsync,
  sendWorkerDepartureEmailsAsync,
} from "../api"
import { useAuth, currentWorkerId } from "../composition-api/useAuth"
import { EMPTY_GUID, DATE_FORMAT_DISPLAY } from "scripts/utils/constants"
import { buildColorMap } from "scripts/utils/colors"
import { formatDuration, createQuickDates } from "scripts/utils/conversions"

defineOptions({ name: "ServiceOrdersMap" })

const { filters, orders, getOrdersAsync, paging, location } = useWorkOrders("Service", () => ({ status: "" }), {
  syncQueryParams: true,
})
const { changeOwnerAsync } = useWorkOrder("Service")
const { statuses: allStatuses, loadEnumsAsync } = useEnums()
const mapEl = ref(null)
const { fetchAuthAsync } = useAuth()
const router = VueRouter.useRouter()
const route = VueRouter.useRoute()
const center = { lat: 46.05, lng: 14.93 }

const statuses = computed(() => {
  const filteredMap = new Map()
  for (const [key, value] of allStatuses.value) {
    if (["Active", "Accepted", "Triage", "InProgress", "Completed", "Finalized"].includes(key)) {
      filteredMap.set(key, value)
    }
  }
  return filteredMap
})

const vehicleLocations = ref([])
const currentLocation = computed(
  () => vehicleLocations.value.find((v) => v.idNaprava == worker.value?.vehicleRegistrationNumber) ?? null,
)

const vehicleHistory = ref([])
const showTodayVehiclePath = ref(false)
const worker = ref(null)
const selectedStartingPoint = ref("vehicle")

const ownerColorMap = computed(() => buildColorMap(orders.value.map((o) => o.ownerID)))

const selectedMarker = ref(null)
const selectedVehicleMarker = ref(null)

const waypoints = ref([])
const savedWaypointsSnapshot = ref(null)
// True only while the in-memory waypoints exactly match the last loaded/saved route. It resets
// automatically on any change (edit, reorder, add/remove, worker/day switch, recalculated times),
// so the route must be saved again before the emails can be sent.
const waypointsSaved = computed(() => {
  const snapshot = savedWaypointsSnapshot.value
  if (snapshot === null) return false
  const current = waypoints.value
  if (current.length !== snapshot.length) return false
  for (let i = 0; i < current.length; i++) {
    if (JSON.stringify(current[i]) !== JSON.stringify(snapshot[i])) return false
  }
  return true
})
const planningEnabled = computed({
  get: () => route.query.planning === "true",
  set: async (val) => {
    const query = { ...route.query }
    if (val) {
      query.planning = "true"
    } else {
      delete query.planning
    }
    await router.replace({ query }).catch(() => {})
    if (val) {
      filters.status = "Accepted"
    }
  },
})
const selectedPlanningDay = ref(dayjs().format("YYYY-MM-DD"))
const isPastDay = computed(() => dayjs(selectedPlanningDay.value).isBefore(dayjs().startOf("day")))
const isToday = computed(() => selectedPlanningDay.value === dayjs().format("YYYY-MM-DD"))

const editingTimeId = ref(null)
const editTimeMinutes = ref(0)
const timeInput = ref(null)

const setTimeInputRef = (el) => {
  timeInput.value = el
}

const startEditingTime = (wp) => {
  editingTimeId.value = wp.id
  editTimeMinutes.value = Math.round((wp.stopSeconds ?? 0) / 60)
  nextTick(() => {
    if (timeInput.value) timeInput.value.focus()
  })
}

const saveEditingTime = (wp) => {
  const idx = waypoints.value.findIndex((w) => w.id === wp.id)
  if (idx !== -1) {
    waypoints.value[idx] = { ...waypoints.value[idx], stopSeconds: editTimeMinutes.value * 60 }
  }
  editingTimeId.value = null
}

const dragState = reactive({ draggingId: null, overId: null })

const onDragStart = (e, orderId) => {
  dragState.draggingId = orderId
  e.dataTransfer.effectAllowed = "move"
}

const onDragOver = (e, orderId) => {
  e.preventDefault()
  e.dataTransfer.dropEffect = "move"
  dragState.overId = orderId
}

const onDrop = (e, targetId) => {
  e.preventDefault()
  const fromId = dragState.draggingId
  if (!fromId || fromId === targetId) return
  const list = [...waypoints.value]
  const fromIdx = list.findIndex((o) => o.id === fromId)
  const toIdx = list.findIndex((o) => o.id === targetId)
  if (fromIdx === -1 || toIdx === -1) return
  const [moved] = list.splice(fromIdx, 1)
  list.splice(toIdx, 0, moved)
  list.forEach((o, i) => {
    o.sequence = i + 1
  })
  waypoints.value = list
}

const onDragEnd = () => {
  dragState.draggingId = null
  dragState.overId = null
}

const loadWaypointsAsync = async () => {
  if (!filters.ownerID || filters.ownerID === EMPTY_GUID || !planningEnabled.value || !selectedPlanningDay.value) {
    waypoints.value = []
    savedWaypointsSnapshot.value = null
    return
  }
  const date = selectedPlanningDay.value
  const saved = await listEntitiesAsync(fetchAuthAsync, `Waypoints/${filters.ownerID}/${date}`)
  const result = (saved || []).map((wp, i) => ({
    id: wp.id || "saved_" + i,
    name: wp.serviceOrderName || wp.name || "",
    lat: wp.lat,
    lng: wp.lng,
    sequence: i + 1,
    serviceOrderID: wp.serviceOrderID !== EMPTY_GUID ? wp.serviceOrderID : null,
    serviceOrderPriority: wp.serviceOrderPriority ?? null,
    serviceOrderIsSpecial: !!wp.serviceOrderIsSpecial,
    serviceOrderIsCompleted: !!wp.serviceOrderIsCompleted,
    serviceOrderIsInProgress: !!wp.serviceOrderIsInProgress,
    stopSeconds: wp.stopSeconds ?? null,
    time: wp.time || null,
  }))
  const dayOrders = orders.value.filter(
    (o) => o.status === "Accepted" && o.datePlannedVisit && dayjs(o.datePlannedVisit).format("YYYY-MM-DD") === date,
  )
  for (const order of dayOrders) {
    if (!result.some((wp) => wp.serviceOrderID === order.id)) {
      result.push({
        id: order.id,
        name: order.facilityName || "",
        lat: order.lat,
        lng: order.lng,
        sequence: result.length + 1,
        serviceOrderID: order.id !== EMPTY_GUID ? order.id : null,
        serviceOrderPriority: order.priority ?? null,
        stopSeconds: order.workAssessmentSeconds,
        serviceOrderIsSpecial: !!order.isSpecial,
        serviceOrderIsInProgress: order.statusText === "InProgress",
        serviceOrderIsCompleted: order.statusText === "Completed" || order.statusText === "Finalized",
      })
    }
  }
  waypoints.value = result
  savedWaypointsSnapshot.value = result.length ? result.map((wp) => ({ ...wp })) : null
}

const isGray = (wp) => {
  if (!wp.serviceOrderID) return false
  const order = orders.value.find((o) => o.id === wp.serviceOrderID)
  if (!order) return false
  if (!order.datePlannedVisit) return true
  return (
    order.ownerID === "CAE80297-AB79-4DFE-03A2-08D6171D86DC" &&
    dayjs(order.datePlannedVisit).isBefore(dayjs().startOf("day"))
  )
}

watch(
  [planningEnabled, selectedPlanningDay, () => filters.ownerID, () => orders.value.length, () => filters.status],
  loadWaypointsAsync,
  { immediate: true },
)

const extraWaypoints = [
  { name: "Skladišče Žalec", lat: 46.2502209, lng: 15.167279 },
  { name: "Skladišče Ajdovščina", lat: 45.886674, lng: 13.9035966 },
]

// --- Google Places Autocomplete (New) ---
const addressSearchContainer = ref(null)
const selectedPlace = ref(null)
let autocompleteElement = null

const initAutocomplete = () => {
  const container = addressSearchContainer.value
  if (!container || autocompleteElement) return
  try {
    autocompleteElement = new google.maps.places.PlaceAutocompleteElement({
      includedRegionCodes: ["SI"],
    })
    autocompleteElement.id = "address-search-input"
    container.appendChild(autocompleteElement)

    autocompleteElement.addEventListener("gmp-select", async (event) => {
      const place = event.placePrediction.toPlace()
      await place.fetchFields({
        fields: ["location", "displayName", "formattedAddress"],
      })
      if (!place.location) {
        setWarning("Izberite veljaven naslov s seznama.")
        selectedPlace.value = null
        return
      }
      selectedPlace.value = {
        name: place.displayName || place.formattedAddress || "",
        lat: place.location.lat(),
        lng: place.location.lng(),
      }
    })

    autocompleteElement.addEventListener("gmp-error", (event) => {
      console.error("PlaceAutocompleteElement error:", event)
      setWarning("Napaka pri iskanju naslova.")
    })
  } catch (e) {
    console.error("PlaceAutocompleteElement init error:", e)
  }
}

const addAddressToWaypoints = () => {
  if (!selectedPlace.value) return
  waypoints.value.push({
    id: "addr_" + Date.now() + "_" + Math.random().toString(36).slice(2, 8),
    name: selectedPlace.value.name,
    lat: selectedPlace.value.lat,
    lng: selectedPlace.value.lng,
    sequence: waypoints.value.length + 1,
    serviceOrderID: null,
    serviceOrderPriority: null,
    serviceOrderIsSpecial: false,
    stopSeconds: 15 * 60,
  })
  selectedPlace.value = null
  // Clear the autocomplete input
  if (autocompleteElement) {
    autocompleteElement.value = ""
  }
}
// --- end Places Autocomplete ---

const addCustomWaypoint = (e) => {
  const idx = parseInt(e.target.value)
  if (idx < 0) return
  const wp = extraWaypoints[idx]
  waypoints.value.push({
    id: "extra_" + Date.now() + "_" + Math.random().toString(36).slice(2, 8),
    name: wp.name,
    lat: wp.lat,
    lng: wp.lng,
    sequence: waypoints.value.length + 1,
    serviceOrderID: null,
    serviceOrderPriority: null,
    serviceOrderIsSpecial: false,
    serviceOrderIsCompleted: false,
    stopSeconds: 15 * 60,
  })
  e.target.value = "-1"
}
const startingPoint = computed(() => {
  if (selectedStartingPoint.value === "vehicle" && currentLocation.value) {
    return { name: "Current Location", lat: currentLocation.value.wy, lng: currentLocation.value.wx }
  }
  if (selectedStartingPoint.value === "home" && worker.value?.lat && worker.value?.lng) {
    return { name: "Home", lat: worker.value.lat, lng: worker.value.lng }
  }
  if (selectedStartingPoint.value === "zalec") {
    return extraWaypoints[0]
  }
  if (selectedStartingPoint.value === "ajdovscina") {
    return extraWaypoints[1]
  }
  // Auto-fallback when selected option's data isn't ready
  if (currentLocation.value)
    return { name: "Current Location", lat: currentLocation.value.wy, lng: currentLocation.value.wx }
  if (worker.value?.lat && worker.value?.lng) return { name: "Home", lat: worker.value.lat, lng: worker.value.lng }
  return extraWaypoints[1]
})

const saveCurrentWaypointsAsync = async () => {
  const items = waypoints.value
  if (!filters.ownerID || filters.ownerID === EMPTY_GUID) {
    console.error("Cannot save group order: ownerID is not set")
    return
  }

  const day = selectedPlanningDay.value
  const wpPayload = items
    .filter((o) => o.lat && o.lng)
    .map((o, i) => {
      const leg = todayRouteLegByOrderId.value?.[o.id]
      return {
        serviceOrderID: o.serviceOrderID,
        name: o.name || "",
        sequence: i + 1,
        lat: o.lat,
        lng: o.lng,
        durationSeconds: leg?.durationSeconds ?? 0,
        distanceMeters: leg?.distanceMeters ?? 0,
        stopSeconds: o.stopSeconds ?? null,
        time: o.time,
      }
    })
  await saveWaypointsAsync(fetchAuthAsync, filters.ownerID, day, wpPayload)
  savedWaypointsSnapshot.value = items.map((wp) => ({ ...wp }))
  await reloadAsync()
}

const sendingDepartureEmails = ref(false)
const sendDepartureEmailsAsync = async () => {
  if (!filters.ownerID || filters.ownerID === EMPTY_GUID) {
    setWarning("Izberite delavca.")
    return
  }
  if (!isToday.value) {
    setWarning("Obvestila lahko pošlješ samo za današnji dan.")
    return
  }
  if (selectedStartingPoint.value !== "vehicle") {
    setWarning("Obvestila lahko pošlješ samo z izhodiščem 'trenutna lokacija vozila'.")
    return
  }
  if (!waypointsSaved.value) {
    setWarning("Najprej shranite pot za izbranega delavca.")
    return
  }
  sendingDepartureEmails.value = true
  try {
    const raw = await sendWorkerDepartureEmailsAsync(fetchAuthAsync, filters.ownerID, dayjs().format("YYYY-MM-DD"))
    if (!raw) return
    if (raw.status !== "ok") {
      setWarning(raw.message || "Pošiljanje e-pošt ni uspelo.")
      return
    }
    const departure = raw.departure ? dayjs.utc(raw.departure).local().format("HH:mm") : ""
    if (raw.skipped > 0) {
      setWarning(
        `Nekatera obvestila o obiskih niso bila poslana (objekt nima konfiguriranega prejemnika ali pošiljanje ni uspelo). Poslanih: ${raw.customerEmailsSent}.`,
      )
      return
    }
    setSuccess(
      `E-pošte poslane za ${raw.worker} (odhod ${departure}); obvestila strankam: ${raw.customerEmailsSent}.`,
    )
  } catch (e) {
    setError(e)
  } finally {
    sendingDepartureEmails.value = false
  }
}

const assignToAsync = async (req) => {
  if (!req?.order || !req?.selectedWorker) {
    console.warn("assignToAsync: invalid request", req)
    return
  }
  if (await changeOwnerAsync(req.order.id, req.selectedWorker)) {
    await reloadAsync()
  }
}

const handleSetDatePlannedVisitAsync = async ({ order, datePlannedVisit }) => {
  selectedMarker.value = null
  if (await patchWorkOrderAsync(fetchAuthAsync, { datePlannedVisit }, "Service", order.id)) {
    await reloadAsync()
    await loadWaypointsAsync()
  }
}

watch(
  () => [filters.completedOrFinalizedYesterday, filters.completedOrFinalizedToday],
  ([yesterday, today], [prevYesterday, prevToday]) => {
    if (yesterday && !prevYesterday) filters.completedOrFinalizedToday = false
    if (today && !prevToday) filters.completedOrFinalizedYesterday = false
    const s = filters.status.split(",").filter(Boolean)
    if (yesterday || today) {
      if (!s.includes("Completed")) s.push("Completed")
      if (!s.includes("Finalized")) s.push("Finalized")
    } else {
      const idx1 = s.indexOf("Completed")
      if (idx1 > -1) s.splice(idx1, 1)
      const idx2 = s.indexOf("Finalized")
      if (idx2 > -1) s.splice(idx2, 1)
    }
    if (filters.status !== s.toString()) filters.status = s.toString()
  },
)

// quick filter — only clear flags, never set them, to avoid ping-pong
watch(
  () => filters.status,
  (status) => {
    const s = status.split(",").filter(Boolean)
    if (!s.includes("Completed") || !s.includes("Finalized")) {
      if (filters.completedOrFinalizedYesterday && !filters.completedOrFinalizedToday) {
        // user manually removed Completed/Finalized from status, clear the checkboxes
        filters.completedOrFinalizedYesterday = false
        filters.completedOrFinalizedToday = false
      }
    }
  },
)

const createMarkerElement = (
  color,
  count = null,
  isSpecial = false,
  shape = "circle",
  priority = null,
  hasNoPlannedDate = false,
) => {
  const wrapper = document.createElement("div")
  wrapper.classList.add("map-marker-wrapper")
  const element = document.createElement("div")
  element.classList.add("map-marker")
  if (shape === "square") element.classList.add("map-marker-square")
  else if (shape === "triangle") element.classList.add("map-marker-triangle")
  if (hasNoPlannedDate) element.classList.add("map-marker-no-date")
  element.style.backgroundColor = color
  element.textContent = count?.toString()
  wrapper.appendChild(element)
  if (isSpecial) {
    const badge = document.createElement("div")
    badge.classList.add("map-special-badge")
    wrapper.appendChild(badge)
  }
  if (priority !== null) {
    const priorityBadge = document.createElement("div")
    priorityBadge.classList.add("map-priority-badge")
    if (priority === 1) priorityBadge.classList.add("map-priority-badge-danger")
    priorityBadge.textContent = priority.toString()
    wrapper.appendChild(priorityBadge)
  }
  return wrapper
}

const markers = computed(() => {
  const colorMap = ownerColorMap.value // read reactive deps explicitly so Vue always tracks them
  const resolveColor = (order) => {
    if (order.status === "Finalized") return "#2E7D32"
    if (order.status === "Completed") return "#66BB6A"
    return colorMap.get(order.ownerID) || "#6c757d"
  }
  const noPlannedDate = (order) =>
    !order.datePlannedVisit && (order.status === "Active" || order.status === "Accepted")

  return orders.value
    .filter((order) => order.lat && order.lng)
    .map((order) => ({
      options: {
        position: { lat: order.lat, lng: order.lng },
        title: order.facilityName,
        content: createMarkerElement(
          resolveColor(order),
          null,
          order.isSpecial,
          "square",
          order.priority,
          noPlannedDate(order),
        ),
      },
      orders: [order],
      isSpecial: order.isSpecial,
      hasNoPlannedDate: noPlannedDate(order),
      isService: order.isService,
      isCondenserCleaning: order.isCondenserCleaning,
      isLeakageTest: order.isLeakageTest,
    }))
    .reduce((acc, curr) => {
      const existing = acc.find(
        (marker) =>
          marker.options.position.lat === curr.options.position.lat &&
          marker.options.position.lng === curr.options.position.lng,
      )
      if (existing) {
        existing.orders.push(curr.orders[0])
        const hasSpecial = existing.orders.some((o) => o.isSpecial)
        const hasNoPlanned = existing.orders.some((o) => noPlannedDate(o))
        const minPriority = Math.min(...existing.orders.map((o) => o.priority || 3))
        existing.options.content = createMarkerElement(
          resolveColor(existing.orders[0]),
          existing.orders.length,
          hasSpecial,
          "square",
          minPriority,
          hasNoPlanned,
        )
        existing.isSpecial = hasSpecial
        existing.hasNoPlannedDate = hasNoPlanned
      } else {
        acc.push(curr)
      }
      return acc
    }, [])
})

const vehicleMarkers = computed(() =>
  vehicleLocations.value
    .filter(
      (vehicle) =>
        !!vehicle &&
        (!worker.value?.vehicleRegistrationNumber || vehicle.idNaprava === worker.value?.vehicleRegistrationNumber),
    )
    .map((vehicle) => {
      return {
        options: {
          position: { lat: vehicle.wy, lng: vehicle.wx },
          title: vehicle.opis || vehicle.name || "Vehicle",
          content: createMarkerElement("#007bff", null, false, "triangle"),
        },
        vehicle,
      }
    }),
)

const homeMarkers = computed(() =>
  vehicleLocations.value
    .filter((vehicle) => vehicle.currentWorker?.lat && vehicle.currentWorker?.lng)
    .filter((vehicle) => vehicle.idNaprava === worker.value?.vehicleRegistrationNumber)
    .map((vehicle) => ({
      options: {
        position: { lat: vehicle.currentWorker.lat, lng: vehicle.currentWorker.lng },
        title: "Home",
        content: createMarkerElement("#28a745"),
      },
    })),
)

const loadVehicleHistoryAsync = async () => {
  if (!showTodayVehiclePath.value) {
    vehicleHistory.value = []
    return
  }
  if (!worker.value?.vehicleRegistrationNumber) {
    vehicleHistory.value = []
    return
  }
  const date = (filters.completedOrFinalizedYesterday ? dayjs().subtract(1, "day") : dayjs()).format("YYYY-MM-DD")
  const raw = await listEntitiesAsync(
    fetchAuthAsync,
    `VehicleHistory/${worker.value?.vehicleRegistrationNumber}`,
    `?date=${date}`,
  )
  vehicleHistory.value =
    raw
      ?.filter((loc) => loc.wy && loc.wx)
      ?.map((loc) => ({ lat: loc.wy, lng: loc.wx, cas: loc.cas, stopduration: loc.stopduration })) ?? []
}

watch(
  () => filters.ownerID,
  async (newId) => {
    selectedStartingPoint.value = "vehicle"
    worker.value = newId && newId !== EMPTY_GUID ? await getEntityAsync(fetchAuthAsync, "Worker", newId) : null
    await loadVehicleHistoryAsync()
  },
  { immediate: true },
)

watch(showTodayVehiclePath, loadVehicleHistoryAsync)

const historyEndpointMarkers = computed(() => {
  if (vehicleHistory.value.length === 0) return []
  const make = (loc, color, label) => ({
    options: {
      position: loc,
      title: label + " " + new Date(loc.cas).toLocaleTimeString(),
      content: createMarkerElement(color, null, false, "triangle"),
    },
  })
  const result = [make(vehicleHistory.value[0], "#28a745", "Start")]
  if (vehicleHistory.value.length > 1)
    result.push(make(vehicleHistory.value[vehicleHistory.value.length - 1], "#dc3545", "End"))
  return result
})

const showRoutePlanning = computed(() => {
  return planningEnabled.value && filters.ownerID
})

const totals = computed(() => {
  const list = waypoints.value
  let roadSeconds = 0
  let roadDistanceMeters = 0
  let workSeconds = 0
  for (const o of list) {
    workSeconds += o.stopSeconds ?? 0
    const leg = (todayRouteLegByOrderId.value || {})[o.id]
    if (leg?.durationSeconds) roadSeconds += leg.durationSeconds
    if (leg?.distanceMeters) roadDistanceMeters += leg.distanceMeters
  }
  const bufferSeconds = list.length * 15 * 60
  return { roadSeconds, roadDistanceMeters, workSeconds, bufferSeconds }
})

const todayRouteLegs = ref([])
const todayRouteLegByOrderId = ref({})

const formatLegInfo = (legData) => {
  if (!legData || !legData.distanceMeters) return ""
  const km = (legData.distanceMeters / 1000).toFixed(0) + " km"
  const dur = formatDuration(legData.durationSeconds)
  return `${km} (${dur})`
}

const calculateTodayRouteAsync = async () => {
  const ordersWithCoords = waypoints.value.filter((o) => o.lat && o.lng)

  if (ordersWithCoords.length < (startingPoint.value ? 1 : 2) || !showRoutePlanning.value) {
    todayRouteLegs.value = []
    todayRouteLegByOrderId.value = {}
    return
  }
  try {
    const { Route } = await google.maps.importLibrary("routes")
    const origin = startingPoint.value ?? { lat: ordersWithCoords[0].lat, lng: ordersWithCoords[0].lng }
    const destination = ordersWithCoords[ordersWithCoords.length - 1]
    const intermediateOrders = ordersWithCoords.slice(startingPoint.value ? 0 : 1, -1).slice(0, 25)
    const { routes } = await Route.computeRoutes({
      origin,
      destination,
      intermediates: intermediateOrders.map((o) => ({ lat: o.lat, lng: o.lng })),
      travelMode: "DRIVING",
      optimizeWaypointOrder: false,
      fields: ["path", "legs"],
    })
    if (!routes?.length || !routes[0].legs?.length) {
      todayRouteLegs.value = []
      todayRouteLegByOrderId.value = {}
      return
    }
    const [fullPolyline] = routes[0].createPolylines()
    const fullPath = fullPolyline
      .getPath()
      .getArray()
      .map((p) => ({ lat: p.lat(), lng: p.lng() }))
    const legs = routes[0].legs
    const arrowIcon = {
      path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
      scale: 3,
      strokeColor: "#0d6efd",
      fillOpacity: 1,
    }

    // Find split indices by searching forward for each intermediate waypoint
    let searchFrom = 0
    const boundaries = [0]
    for (const wp of intermediateOrders) {
      let minDist = Infinity
      let minIdx = searchFrom
      for (let j = searchFrom; j < fullPath.length; j++) {
        const d = (fullPath[j].lat - wp.lat) ** 2 + (fullPath[j].lng - wp.lng) ** 2
        if (d < minDist) {
          minDist = d
          minIdx = j
        }
      }
      boundaries.push(minIdx)
      searchFrom = minIdx
    }

    todayRouteLegs.value = legs.map((leg, i) => ({
      path: fullPath.slice(boundaries[i], boundaries[i + 1] ?? fullPath.length),
      icons: [{ icon: arrowIcon, offset: "50%" }],
    }))
    const legByOrderId = {}
    const ordersForLegs = startingPoint.value ? ordersWithCoords : ordersWithCoords.slice(1)
    ordersForLegs.forEach((order, i) => {
      legByOrderId[order.id] = {
        distanceMeters: legs[i]?.distanceMeters ?? 0,
        durationSeconds: legs[i]?.durationMillis ? Math.round(legs[i].durationMillis / 1000) : 0,
      }
    })
    todayRouteLegByOrderId.value = legByOrderId
  } catch (e) {
    console.error(e)
    setWarning("Pri izrisu poti je prišlo do napake. Poskusite znova.")
    todayRouteLegs.value = []
    todayRouteLegByOrderId.value = {}
  }
}

const recalculateVisitTimes = () => {
  const list = waypoints.value
  if (!list.length) return
  const sorted = [...list].sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
  let totalSeconds = 7 * 60 * 60 // start
  for (const wp of sorted) {
    const leg = (todayRouteLegByOrderId.value || {})[wp.id]
    const travelSeconds = leg?.durationSeconds ?? 0
    totalSeconds += travelSeconds
    const h = Math.floor(totalSeconds / 3600)
    const m = Math.floor((totalSeconds % 3600) / 60)
    wp.time = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`
    totalSeconds += wp.stopSeconds || 0
    totalSeconds += 15 * 60 // buffer between stops
  }
}

watch([waypoints, startingPoint], calculateTodayRouteAsync, { deep: true })

watch([waypoints, todayRouteLegByOrderId], recalculateVisitTimes, { deep: true })

let fetchingByLocation = false
watch(currentLocation, async (loc) => {
  if (fetchingByLocation) return
  fetchingByLocation = true
  location.value = loc?.wy && loc?.wx ? { lat: loc.wy, lng: loc.wx } : { lat: null, lng: null }
  try {
    await getOrdersAsync()
  } finally {
    fetchingByLocation = false
  }
})

let _reloading = false
const reloadAsync = async () => {
  if (_reloading) return
  _reloading = true
  try {
    await getOrdersAsync()
    await loadVehicleHistoryAsync()
    await loadVehicleLocationsAsync()
  } finally {
    _reloading = false
  }
}

const loadVehicleLocationsAsync = async () => {
  const raw = await listEntitiesAsync(fetchAuthAsync, "VehicleLocations")
  vehicleLocations.value = raw?.filter((loc) => loc.wy && loc.wx) ?? []
}

onUnmounted(() => {})

onMounted(async () => {
  startLoading("Orders")
  try {
    await loadEnumsAsync()
    paging.value.itemsPerPage = 1000
    await reloadAsync()
  } catch (e) {
    setError(e)
  }
  stopLoading("Orders")
})

// Initialize PlaceAutocompleteElement once the planning sidebar becomes visible
watch(showRoutePlanning, async (visible) => {
  if (visible) {
    await nextTick()
    initAutocomplete()
  }
})
</script>
